import { Component, computed, inject, input, linkedSignal, signal } from '@angular/core';
import { Location, locationToModel } from './definitions';
import { FormField, form, required } from '@angular/forms/signals';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Button, InputSimple, NotificationService } from 'excellence-ui';
import { NgIcon } from '@ng-icons/core';
import { FieldErrors } from '../../../../shared/components/field-errors/field-errors';
import { LocationDetailsEditSuccessNotification } from '../../notifications/location-details-edit-success-notification/location-details-edit-success-notification';
import { LocationResponse } from '../../../../core/clients/omni-core/models';
import { LocationsStore } from '../../store/locations.store';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-location-details-edit-form',
  imports: [TranslocoPipe, Button, NgIcon, InputSimple, FormField, FieldErrors],
  templateUrl: './location-details-edit-form.html',
})
export class LocationDetailsEditForm {
  private store = inject(LocationsStore);
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);

  location = input.required<LocationResponse>();
  private locationData = computed(() => locationToModel(this.location()));
  organizationId = input.required<string>();
  locationId = input.required<string>();

  private state = computed(() =>
    this.store.locations()?.find((loc) => loc.id === this.locationId()),
  );

  protected editButtonLoading = signal(false);
  protected enableEdit = signal(false);
  protected isUpdateLoading = computed(() => this.state()?.isEditLoading ?? false);

  protected model = linkedSignal<Location>(() => locationToModel(this.location()));

  protected form = form(this.model, (schemaPath) => {
    required(schemaPath.address.street);
    required(schemaPath.address.streetNumber);
    required(schemaPath.address.zipCode);
    required(schemaPath.address.city);
  });

  protected onClickCancel() {
    this.enableEdit.set(false);
    this.model.set(this.locationData());
  }

  protected async onClickEnableEdit() {
    this.editButtonLoading.set(true);
    await new Promise((resolve) => setTimeout(resolve, environment.delays.editLocationMs));
    this.enableEdit.set(true);
    this.editButtonLoading.set(false);
  }

  protected async onSubmit() {
    if (this.form().invalid()) {
      return;
    }
    try {
      await this.store.update(
        this.locationId(),
        {
          ...this.model(),
          name: this.model().name || undefined,
        },
        environment.delays.updateLocationMs,
      );
      this.enableEdit.set(false);
      this.notificationService.show({
        component: LocationDetailsEditSuccessNotification,
        duration: 5000,
        componentProps: {
          clickUndoCallback: () => this.onClickUndo(),
        },
      });
    } catch (e) {
      console.error('Failed to update location', e);
      const errorMsg = this.translocoService.translate(
        'cockpit.locations.details.general.updateError',
      );
      this.notificationService.showError({
        title: errorMsg,
      });
    }
  }

  private onClickUndo() {
    return this.store.undoUpdate(this.locationId(), environment.delays.updateLocationMs);
  }
}
